import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MFASetup from './MFASetup';

interface MFAEnforcementProps {
  children: React.ReactNode;
}

const MFAEnforcement: React.FC<MFAEnforcementProps> = ({ children }) => {
  const { isAdmin, hasMFA, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [showSetup, setShowSetup] = React.useState(false);
  

  // Don't enforce MFA if not an admin or still loading
  if (!isAdmin || loading) {
    return <>{children}</>;
  }

  // If admin but no MFA, show setup screen
  if (!hasMFA) {
    return (
      <>
        {!showSetup && (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md w-full mx-4">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Multi-Factor Authentication Required
              </h2>
              
              <p className="text-gray-600 mb-6">
                For security reasons, all admin accounts must have Multi-Factor Authentication (MFA) enabled. 
                Please set up MFA to continue accessing the admin dashboard.
              </p>
              
              <div className="space-y-4">
                <button
                  onClick={() => {
                    setShowSetup(true);
                  }}
                  className="w-full bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  Setup MFA Now
                </button>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-amber-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-sm text-amber-800">
                      <p className="font-medium mb-1">Security Notice</p>
                      <p>
                        MFA is mandatory for all admin accounts. You cannot access the dashboard until MFA is properly configured.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
        
        {/* MFA Setup Modal */}
        {showSetup && (() => {
          return (
            <MFASetup
              onComplete={async () => {
                setShowSetup(false);
                
                // Sign out the user and redirect to login page
                // This ensures they re-authenticate with their new MFA setup
                try {
                  await signOut();
                  navigate('/', { replace: true });
                } catch (error) {
                  // Fallback: force redirect to login page
                  window.location.href = '/';
                }
              }}
              onCancel={() => {
                setShowSetup(false);
              }}
            />
          );
        })()}
      </>
    );
  }

  // If MFA is enabled, show the normal content
  return (
    <>
      {children}
      {showSetup && (() => {
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'white', padding: '20px', borderRadius: '8px' }}>
              <h2>MFA Setup Modal (Test)</h2>
              <p>Modal is working! showSetup: {showSetup.toString()}</p>
              <button onClick={() => setShowSetup(false)}>Close</button>
            </div>
          </div>
        );
      })()}
    </>
  );
};

export default MFAEnforcement;
